/**
 * Enforces RFQ-like source business rules:
 * 1. If any source is deselected, RFQ-like sources must be deselected
 * 2. RFQ-like sources can only be selected if ALL non-RFQ-like sources are selected
 * 3. RFQ-like sources are all-or-none (no partial selection)
 *
 * @param selectedSources - Array of currently selected source keys
 * @param allAvailableSources - Array of all available source keys for the chain
 * @returns The validated and corrected list of sources to enable
 */
export const RFQ_LIKE_SOURCES: ReadonlyArray<string> = ['0x_RFQ', 'Bebop', 'Native_V2', 'Swaap_V2'];

const RFQ_LIKE_SOURCES_SET = new Set(RFQ_LIKE_SOURCES);
const isRfqLikeSource = (source: string) => RFQ_LIKE_SOURCES_SET.has(source);

export function getAvailableRfqLikeSources(allAvailableSources: string[]) {
  return allAvailableSources.filter(isRfqLikeSource);
}

/**
 * Normalizes RFQ-like sources to behave as an all-or-none selection group.
 * - Selecting any RFQ-like source selects all RFQ-like sources (when allowed by enforceRFQRules)
 * - Deselecting any RFQ-like source deselects all RFQ-like sources
 */
function normalizeRfqLikeSelection(
  previousSelectedSources: string[],
  nextSelectedSources: string[],
  allAvailableSources: string[],
): string[] {
  const availableRfqLikeSources = getAvailableRfqLikeSources(allAvailableSources);
  if (availableRfqLikeSources.length === 0) return nextSelectedSources;

  const previousSelectedSet = new Set(previousSelectedSources);
  const nextSelectedSet = new Set(nextSelectedSources);

  const previouslyHadAny = availableRfqLikeSources.some((source) =>
    previousSelectedSet.has(source),
  );
  const previouslyHadAll = availableRfqLikeSources.every((source) =>
    previousSelectedSet.has(source),
  );

  const nextHasAny = availableRfqLikeSources.some((source) => nextSelectedSet.has(source));
  const nextHasAll = availableRfqLikeSources.every((source) => nextSelectedSet.has(source));

  if (nextHasAny && !nextHasAll) {
    if (!previouslyHadAny) {
      for (const source of availableRfqLikeSources) nextSelectedSet.add(source);
      return [...nextSelectedSet];
    }

    if (previouslyHadAll) {
      return nextSelectedSources.filter((key) => !isRfqLikeSource(key));
    }
  }

  return nextSelectedSources;
}

export function applyRfqLikeSelectionRules(
  previousSelectedSources: string[],
  nextSelectedSources: string[],
  allAvailableSources: string[],
): string[] {
  return enforceRFQRules(
    normalizeRfqLikeSelection(previousSelectedSources, nextSelectedSources, allAvailableSources),
    allAvailableSources,
  );
}

export function enforceRFQRules(
  selectedSources: string[],
  allAvailableSources: string[],
): string[] {
  if (!selectedSources || selectedSources.length === 0) {
    return selectedSources;
  }

  const availableRfqLikeSources = getAvailableRfqLikeSources(allAvailableSources);
  if (availableRfqLikeSources.length === 0) return selectedSources;

  const selectedSet = new Set(selectedSources);
  const hasAnyRfqLikeSourceSelected = availableRfqLikeSources.some((source) =>
    selectedSet.has(source),
  );
  if (!hasAnyRfqLikeSourceSelected) return selectedSources;

  const hasAllRfqLikeSourcesSelected = availableRfqLikeSources.every((source) =>
    selectedSet.has(source),
  );

  const nonRfqLikeSources = allAvailableSources.filter((key) => !isRfqLikeSource(key));
  const allNonRfqLikeSourcesSelected = nonRfqLikeSources.every((source) => selectedSet.has(source));

  if (!allNonRfqLikeSourcesSelected || !hasAllRfqLikeSourcesSelected) {
    return selectedSources.filter((source) => !isRfqLikeSource(source));
  }

  return selectedSources;
}

/**
 * Validates and enforces excluded sources for API endpoints.
 * If ANY source is excluded, ensure all RFQ-like sources are also excluded.
 *
 * @param excludedSources - Comma-separated string of excluded source names
 * @returns Validated comma-separated string with RFQ-like rules enforced
 */
export function validateExcludedSources(excludedSources?: string): string | undefined {
  if (!excludedSources) return excludedSources;

  const excludedArray = excludedSources
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (excludedArray.length === 0) return undefined;

  const validated = new Set(excludedArray);
  for (const rfqLikeSource of RFQ_LIKE_SOURCES) validated.add(rfqLikeSource);
  return [...validated].join(',');
}
